/**
 * ReattachAccountModal — picker + confirm modal for reattaching an orphan
 * Inactive Account back to a parent (#392 / #404, 2026-06-09).
 *
 * Two variants driven by `target`:
 *   • 'agreement' → AgreementPicker → orphan + Inactive → Active Direct.
 *   • 'policy'    → InsurancePolicyPicker → orphan + Inactive → Active Indirect.
 *
 * §3g classification — Session A4 (2026-06-10, old #416 / C-111):
 * converted to Pattern A by default. When the parent page passes `onStage`,
 * the Confirm button STAGES the reattach on the page's Edit transaction
 * (commits on Save Changes, clears on Discard) — resolving the C-111
 * "zombie Edit mode" gotcha structurally: the page's Save handler owns the
 * Edit-mode exit. When `onStage` is omitted, the modal keeps the legacy
 * immediate-commit (Pattern B) behaviour for callsites outside an
 * Edit-mode transaction (mirrors DetachPolicyModal's dual mode from A2).
 *
 * Friction model mirrors DetachPolicyModal: clear preview of the
 * destination state + explicit Confirm gate. Charter §5
 * (human-in-the-loop) — lifecycle state transitions need a deliberate yes.
 */
import {
  Alert, Badge, Button, Group, Modal, SegmentedControl, Stack, Text, TextInput, Title,
} from '@mantine/core';
import { IconAlertTriangle, IconLink, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';

import { ApiError } from '../api/client';
import type { Account, UUID } from '../api/types';
import {
  useAttachAccountToAgreement,
} from '../hooks/useAccounts';
import { useAttachAccountToPolicy } from '../hooks/useAccountInsurance';
import { useAlertModal } from './AlertModalProvider';
import { AgreementPicker, InsurancePolicyPicker } from './EntityPicker';

/**
 * #18 (2026-06-10): draft for the create-new-policy-and-attach flow.
 * Staged on the Account page's Edit transaction; on Save the page calls
 * accountInsuranceApi.create with `account_ids: [account.id]`, which the
 * backend applies atomically — the new Policy AND the orphan promotion to
 * Indirect land under one Compliance approval (single CR for RM filings;
 * see #274 Wave A.2 backend side-channel).
 */
export interface NewPolicyDraft {
  agreement_id: UUID;
  insurer: string;
  policy_number: string;
  fund_type: string;
  fund_number: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  account: Account;
  target: 'agreement' | 'policy';
  /** Optional success handler — used by the detail page to refresh local
   *  UI or navigate after the reattach lands. Only fires on the legacy
   *  immediate-commit path (when `onStage` is not provided). */
  onSuccess?: () => void;
  /**
   * Session A4 (old #416): Pattern A staging path. When provided, Confirm
   * does NOT call the API — it hands the picked target back to the parent
   * page, which stages it on the Edit transaction and commits on Save.
   */
  onStage?: (target: 'agreement' | 'policy', targetId: UUID) => void;
  /**
   * #18: create-new-policy-and-attach staging path. Only meaningful for
   * target='policy'. When provided, the modal offers an "Existing | Create
   * new" choice; Create new collects the owning Agreement (required
   * post-0028) + minimal policy identity and stages the draft.
   */
  onStageCreateNew?: (draft: NewPolicyDraft) => void;
}

export function ReattachAccountModal({
  opened, onClose, account, target, onSuccess, onStage, onStageCreateNew,
}: Props) {
  const [targetId, setTargetId] = useState<UUID | null>(null);
  // #18: 'existing' = pick an Active policy; 'create' = stage a new one.
  const [mode, setMode] = useState<'existing' | 'create'>('existing');
  const [draftAgreementId, setDraftAgreementId] = useState<UUID | null>(null);
  const [draftInsurer, setDraftInsurer] = useState('');
  const [draftPolicyNumber, setDraftPolicyNumber] = useState('');
  const [draftFundType, setDraftFundType] = useState('');
  const [draftFundNumber, setDraftFundNumber] = useState('');
  const attachAgreement = useAttachAccountToAgreement();
  const attachPolicy = useAttachAccountToPolicy();
  const { showAlert } = useAlertModal();

  const isAgreement = target === 'agreement';
  const isCreateMode = !isAgreement && mode === 'create' && !!onStageCreateNew;
  const mut = isAgreement ? attachAgreement : attachPolicy;

  function handleClose() {
    setTargetId(null);
    setMode('existing');
    setDraftAgreementId(null);
    setDraftInsurer('');
    setDraftPolicyNumber('');
    setDraftFundType('');
    setDraftFundNumber('');
    onClose();
  }

  function handleConfirm() {
    // #18: create-new path stages the draft and exits — validation is the
    // button's disabled state (agreement + insurer + policy number).
    if (isCreateMode) {
      if (!draftAgreementId || !draftInsurer.trim() || !draftPolicyNumber.trim()) return;
      onStageCreateNew!({
        agreement_id: draftAgreementId,
        insurer: draftInsurer.trim(),
        policy_number: draftPolicyNumber.trim(),
        fund_type: draftFundType.trim(),
        fund_number: draftFundNumber.trim(),
      });
      handleClose();
      return;
    }
    if (!targetId) return;
    // Pattern A path (Session A4): stage on the parent page's Edit
    // transaction; the page's Save Changes commits it.
    if (onStage) {
      onStage(target, targetId);
      handleClose();
      return;
    }
    if (isAgreement) {
      attachAgreement.mutate(
        { id: account.id, agreementId: targetId },
        {
          onSuccess: () => { handleClose(); onSuccess?.(); },
          onError: (err) => {
            if (err instanceof ApiError) {
              showAlert({
                color: 'red',
                title: 'Cannot attach Account to Agreement',
                message: err.detail,
              });
            }
          },
        },
      );
    } else {
      attachPolicy.mutate(
        { policy_id: targetId, account_id: account.id },
        {
          onSuccess: () => { handleClose(); onSuccess?.(); },
          onError: (err) => {
            if (err instanceof ApiError) {
              showAlert({
                color: 'red',
                title: 'Cannot attach Account to Policy',
                message: err.detail,
              });
            }
          },
        },
      );
    }
  }

  // Pedro (2026-06-10): "Attach", not "Reattach" — the policy flavour now
  // also covers creating a brand-new policy (#18).
  const title = isAgreement
    ? 'Attach Account to an Agreement'
    : 'Attach Account to a Policy';
  const destinationLabel = isAgreement ? 'Agreement' : 'Insurance Policy';
  const resultBadgeColor = 'green';
  const resultBadgeLabel = isAgreement ? 'Active Direct' : 'Active Indirect';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="lg"
      // Old #417 (Pedro, flagged repeatedly; fixed 2026-06-10): the modal
      // sized itself to its short content, so the picker's dropdown opened
      // into a clipped few-pixel strip. A body min-height gives the
      // dropdown room to render inside the modal.
      styles={{ body: { minHeight: 420 } }}
      title={
        <Group gap="xs">
          <IconLink size={20} />
          <Title order={4}>{title}</Title>
        </Group>
      }
    >
      <Stack>
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            This Account is currently <Badge color="yellow" variant="light">orphan + Inactive</Badge>.
            Attaching to {isAgreement ? 'an Agreement' : 'a Policy'} promotes it
            to <Badge color={resultBadgeColor} variant="light">{resultBadgeLabel}</Badge>.
            {onStage && ' The attach is staged and only commits when you click Save Changes on the account page.'}
          </Text>
        </Alert>

        {/* #18: for the policy flavour, offer attach-to-existing OR
            create-new-and-attach (when the page provides the staging
            callback). The choice drives which fields render below. */}
        {!isAgreement && onStageCreateNew && (
          <SegmentedControl
            value={mode}
            onChange={(v) => setMode(v as 'existing' | 'create')}
            data={[
              { value: 'existing', label: 'Existing policy' },
              { value: 'create', label: 'Create new policy' },
            ]}
          />
        )}

        {isAgreement ? (
          <AgreementPicker
            label="Target Agreement"
            description="Pick an Active Agreement to reattach this Account to. Only Active Agreements are valid targets."
            value={targetId}
            onChange={setTargetId}
            required
          />
        ) : isCreateMode ? (
          <>
            <AgreementPicker
              label="Owning Agreement for the new Policy"
              description="Every Policy belongs to an Agreement. The new Policy is created under this Agreement, and this Account attaches to it as Indirect."
              value={draftAgreementId}
              onChange={setDraftAgreementId}
              required
            />
            <Group grow>
              <TextInput
                label="Insurer"
                placeholder="e.g. Allianz Vida Portugal"
                value={draftInsurer}
                onChange={(e) => setDraftInsurer(e.currentTarget.value)}
                required
              />
              <TextInput
                label="Policy number"
                placeholder="e.g. POL-100384"
                value={draftPolicyNumber}
                onChange={(e) => setDraftPolicyNumber(e.currentTarget.value)}
                required
              />
            </Group>
            <Group grow>
              <TextInput
                label="Fund type"
                description="Optional"
                value={draftFundType}
                onChange={(e) => setDraftFundType(e.currentTarget.value)}
              />
              <TextInput
                label="Fund number"
                description="Optional"
                value={draftFundNumber}
                onChange={(e) => setDraftFundNumber(e.currentTarget.value)}
              />
            </Group>
            <Text size="xs" c="dimmed">
              The Policy is created and this Account attached in one operation
              when you click Save Changes — one approval covers both.
            </Text>
          </>
        ) : (
          <InsurancePolicyPicker
            label="Target Insurance Policy"
            description="Pick an Active Policy to reattach this Account to as Indirect. Only Active Policies are valid targets."
            value={targetId}
            onChange={setTargetId}
            required
          />
        )}

        <Group justify="space-between" pt="sm">
          <Button variant="default" onClick={handleClose}
            disabled={mut.isPending}>
            Cancel
          </Button>
          <Button color="green"
            leftSection={isCreateMode ? <IconPlus size={14} /> : <IconLink size={14} />}
            onClick={handleConfirm}
            loading={mut.isPending}
            disabled={isCreateMode
              ? (!draftAgreementId || !draftInsurer.trim() || !draftPolicyNumber.trim())
              : !targetId}>
            {isCreateMode
              ? 'Stage create & attach'
              : onStage ? 'Stage attach' : `Attach to ${destinationLabel}`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
